import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Grid,
  Card,
  Button,
} from '@mui/material';
import {
  ExpandMore,
  Search,
  Help,
  ContactMail,
  ArrowForward,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';

const FAQs = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState(false);

  const faqCategories = [
    {
      category: 'General',
      icon: <Help />,
      faqs: [
        { q: "What services do you offer?", a: "We offer fiber optic connectivity, telecom tower leasing, data center colocation, internet solutions, managed IT services, and enterprise solutions for businesses of all sizes." },
        { q: "Do you operate globally?", a: "Yes, we have presence in 150+ countries across North America, Europe, Asia Pacific, Middle East, Africa, and Latin America." },
        { q: "What is your uptime guarantee?", a: "We offer 99.999% uptime SLA for our enterprise services with financial credits for any downtime." },
      ]
    },
    {
      category: 'Services & Pricing',
      icon: <Help />,
      faqs: [
        { q: "How quickly can you deploy services?", a: "Deployment times vary by service and location, from 2 weeks for dedicated internet to 3-6 months for custom fiber builds." },
        { q: "Do you offer custom solutions?", a: "Yes, our enterprise team works closely with clients to design tailored solutions for their specific needs." },
        { q: "What are your pricing models?", a: "We offer flexible pricing models including monthly subscriptions, annual contracts, and custom enterprise agreements." },
      ]
    },
    {
      category: 'Technical Support',
      icon: <Help />,
      faqs: [
        { q: "Is 24/7 support available?", a: "Yes, we provide 24/7 technical support in 12 languages across all time zones." },
        { q: "How do I report an issue?", a: "You can contact our support team via phone, email, or through our customer portal. Emergency support is available 24/7." },
        { q: "Do you provide SLAs?", a: "Yes, we offer comprehensive SLAs with our enterprise and business plans, including response time guarantees and performance metrics." },
      ]
    },
  ];

  const filteredFaqs = faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq =>
      faq.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.faqs.length > 0);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box>
      <PageHeader 
        title="Frequently Asked Questions" 
        subtitle="Find answers to common questions about our services"
      />

      <Container maxWidth="xl" sx={{ py: 8 }}>
        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Card sx={{ p: 4, mb: 6, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              How can we help you?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Search for answers or browse our categories below
            </Typography>
            <TextField
              fullWidth
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ maxWidth: 500, mx: 'auto' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Card>
        </motion.div>

        {/* FAQ Categories */}
        <Grid container spacing={4}>
          {filteredFaqs.map((category, catIndex) => (
            <Grid item xs={12} key={catIndex}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: catIndex * 0.1 }}
                viewport={{ once: true }}
              >
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
                  {category.category}
                </Typography>
                {category.faqs.map((faq, faqIndex) => (
                  <Accordion
                    key={faqIndex}
                    expanded={expanded === `${catIndex}-${faqIndex}`}
                    onChange={handleChange(`${catIndex}-${faqIndex}`)}
                    sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="h6" fontWeight={500}>
                        {faq.q}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1" color="text.secondary">
                        {faq.a}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Still Have Questions */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Card sx={{ p: 4, mt: 6, textAlign: 'center', bgcolor: theme.palette.primary.main, color: 'white' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Still Have Questions?
            </Typography>
            <Typography variant="body1" paragraph sx={{ opacity: 0.9 }}>
              Can't find the answer you're looking for? Please contact our support team.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/contact')}
              sx={{
                bgcolor: 'white',
                color: theme.palette.primary.main,
                '&:hover': { bgcolor: 'white', transform: 'translateY(-2px)' },
              }}
            >
              Contact Support
            </Button>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default FAQs;